import type { DoseUnit, MassUnit } from './types';

export const LB_PER_KG = 2.20462;
export const CM_PER_IN = 2.54;

export const lbToKg = (lb: number) => lb / LB_PER_KG;
export const kgToLb = (kg: number) => kg * LB_PER_KG;
export const inToCm = (inches: number) => inches * CM_PER_IN;
export const cmToIn = (cm: number) => cm / CM_PER_IN;
export const fToC = (f: number) => ((f - 32) * 5) / 9;
export const cToF = (c: number) => (c * 9) / 5 + 32;

const MG_PER_UNIT: Record<MassUnit, number> = { mcg: 0.001, mg: 1, g: 1000 };

export function isMassUnit(unit: string): unit is MassUnit {
  return unit === 'mcg' || unit === 'mg' || unit === 'g';
}

/** Converts between dose units. Returns null when the units are not interconvertible. */
export function convertAmount(value: number, from: DoseUnit, to: DoseUnit): number | null {
  if (from === to) return value;
  if (isMassUnit(from) && isMassUnit(to)) return (value * MG_PER_UNIT[from]) / MG_PER_UNIT[to];
  return null;
}

/** Oxygen cylinder conversion factors (L/psi). */
export const OXYGEN_CYLINDER_FACTORS = {
  D: 0.16,
  E: 0.28,
  G: 2.41,
  M: 1.56,
  'H/K': 3.14,
} as const;

export type OxygenCylinder = keyof typeof OXYGEN_CYLINDER_FACTORS;

/**
 * Estimated cylinder duration in minutes: (pressure − safe residual) × factor ÷ flow.
 * The default safe residual pressure is 200 psi.
 */
export function oxygenCylinderDuration(
  cylinder: OxygenCylinder,
  pressurePsi: number,
  flowLpm: number,
  safeResidualPsi = 200,
): number {
  if (flowLpm <= 0) return Infinity;
  const usable = Math.max(pressurePsi - safeResidualPsi, 0);
  return (usable * OXYGEN_CYLINDER_FACTORS[cylinder]) / flowLpm;
}
